import type { WhiteboardMark, WhiteboardRenderState } from "@/lib/whiteboard/boardState";
import { normalizeAnswer } from "@/lib/whiteboard/spec";
import type { WhiteboardEventName } from "@/lib/whiteboard/spec";
import type { CSSProperties } from "react";

/** Card-level interaction, before the parent attaches exerciseId/kind. */
export interface WhiteboardCardEvent {
  event: WhiteboardEventName;
  response?: string;
  correct?: boolean;
  action?: string;
  target?: string;
  expected?: string;
  progress?: string;
  resolved?: boolean;
  attempt?: number;
  /** For composed exercises: which block the event came from. */
  block?: string;
  /** Stable target id when the UI can identify the exact visible target. */
  targetId?: string;
}

export type WhiteboardCardEventHandler = (event: WhiteboardCardEvent) => void;

export function simpleWhiteboardTargetId(kind: string, role: string, index?: number): string {
  return index === undefined ? `${kind}.${role}` : `${kind}.${role}.${index}`;
}

export function composedBlockTargetId(
  blockIndex: number,
  role: string,
  index?: number,
  subIndex?: number
): string {
  const base = `block.${blockIndex + 1}.${role}`;
  if (index === undefined) return base;
  return subIndex === undefined ? `${base}.${index}` : `${base}.${index}.${subIndex}`;
}

export function whiteboardWriteDelayStyle(index: number): CSSProperties {
  return {
    "--whiteboard-write-delay": `${80 + Math.max(0, index) * 130}ms`,
  } as CSSProperties;
}

export function isWhiteboardTargetHighlighted(
  value: string,
  renderState: WhiteboardRenderState,
  targetId?: string
): boolean {
  const normalizedValue = normalizeAnswer(value);
  const normalizedTargetId = targetId ? normalizeAnswer(targetId) : "";
  return renderState.highlightTargets.some((target) => {
    const normalizedTarget = normalizeAnswer(target);
    if (normalizedTargetId && normalizedTarget === normalizedTargetId) return true;
    return Boolean(
      normalizedTarget &&
      (normalizedValue.includes(normalizedTarget) || normalizedTarget.includes(normalizedValue))
    );
  });
}

export function getWhiteboardTargetMark(
  value: string,
  renderState: WhiteboardRenderState,
  targetId?: string
): WhiteboardMark | undefined {
  const normalizedValue = normalizeAnswer(value);
  const normalizedTargetId = targetId ? normalizeAnswer(targetId) : "";
  return renderState.marks.find((mark) => {
    if (
      normalizedTargetId &&
      mark.targetId &&
      normalizeAnswer(mark.targetId) === normalizedTargetId
    ) {
      return true;
    }
    const normalizedTarget = normalizeAnswer(mark.target);
    return Boolean(
      normalizedTarget &&
      (normalizedValue.includes(normalizedTarget) || normalizedTarget.includes(normalizedValue))
    );
  });
}

export function getWhiteboardMarkClass(mark: WhiteboardMark | undefined): string {
  if (!mark) return "";
  switch (mark.style) {
    case "circle":
      return "ring-2 ring-[#FD954F]/80 ring-offset-2 ring-offset-[#171717]";
    case "box":
      return "border-[#FD954F]/80 bg-[#FD954F]/10";
    case "arrow":
      return "border-[#FD954F]/70 bg-[#FD954F]/10 shadow-[inset_4px_0_0_rgba(253,149,79,0.9)]";
    case "underline":
    default:
      return "underline decoration-[#FD954F] decoration-4 underline-offset-4";
  }
}
